import { z } from 'zod';
import { validaCodiceFiscale } from './codice-fiscale.js';
import { SCOPI_OTP } from './ruoli.js';

/** Campo CF: normalizza e valida, con il messaggio del brand book. */
export const codiceFiscaleSchema = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const esito = validaCodiceFiscale(v);
    if (!esito.valido) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: esito.messaggio });
      return z.NEVER;
    }
    return esito.codiceFiscale;
  });

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email("L'indirizzo email non sembra completo: controlla la chiocciola e il dominio.")
  .max(254);

export const passwordSchema = z
  .string()
  .min(10, 'La password deve avere almeno 10 caratteri.')
  .max(128, 'La password può avere al massimo 128 caratteri.');

export const codiceOtpSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Il codice ha 6 cifre: controlla l\'email che ti abbiamo inviato.');

const nomeSchema = z.string().trim().min(1, 'Inserisci il nome.').max(100);

export const registrazioneSchema = z.object({
  codiceFiscale: codiceFiscaleSchema,
  nome: nomeSchema,
  cognome: z.string().trim().min(1, 'Inserisci il cognome.').max(100),
  email: emailSchema,
  ordine: z.string().trim().min(1, "Inserisci l'ordine o l'associazione di appartenenza.").max(200),
  numeroAlbo: z.string().trim().max(50).optional().or(z.literal('')),
  /** Iscrizione diretta a un percorso con iscrizione aperta (link /registrati?percorso=ID). */
  percorsoId: z.string().uuid().optional(),
});
export type RegistrazioneInput = z.infer<typeof registrazioneSchema>;

/** Verifica dell'email: codice ricevuto + scelta della password, in un solo passo. */
export const verificaEmailSchema = z.object({
  email: emailSchema,
  codice: codiceOtpSchema,
  password: passwordSchema,
});
export type VerificaEmailInput = z.infer<typeof verificaEmailSchema>;

export const accessoSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Inserisci la password.'),
});
export type AccessoInput = z.infer<typeof accessoSchema>;

export const nuovoDispositivoSchema = z.object({
  email: emailSchema,
  codice: codiceOtpSchema,
});
export type NuovoDispositivoInput = z.infer<typeof nuovoDispositivoSchema>;

export const reinviaCodiceSchema = z.object({
  email: emailSchema,
  scopo: z.enum(SCOPI_OTP),
});
export type ReinviaCodiceInput = z.infer<typeof reinviaCodiceSchema>;

/** Risposta di GET /me e degli accessi riusciti. Mai il CF per intero fuori dall'admin. */
export const utenteCorrenteSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  ruolo: z.enum(['partecipante', 'relatore', 'regia', 'admin']),
  ordine: z.string().nullable(),
  numeroAlbo: z.string().nullable(),
  codiceFiscaleMascherato: z.string(), // es. RSSMRA******223V
  /** secondo fattore (app di autenticazione) attivo */
  totpAttivo: z.boolean().default(false),
  /** admin senza secondo fattore in un ambiente che lo richiede: l'area amministratore è bloccata finché non lo attiva */
  totpRichiesto: z.boolean().default(false),
});
export type UtenteCorrente = z.infer<typeof utenteCorrenteSchema>;

/** Esito di POST /auth/accesso: sessione aperta, oppure serve il codice del nuovo dispositivo. */
export const esitoAccessoSchema = z.discriminatedUnion('esito', [
  z.object({ esito: z.literal('ok'), utente: utenteCorrenteSchema }),
  z.object({ esito: z.literal('codice_richiesto'), email: z.string() }),
  /** amministratori con TOTP attivo: serve il codice dell'app di autenticazione */
  z.object({ esito: z.literal('totp_richiesto'), email: z.string() }),
]);
export type EsitoAccesso = z.infer<typeof esitoAccessoSchema>;

export function mascheraCodiceFiscale(cf: string): string {
  return cf.length === 16 ? `${cf.slice(0, 6)}******${cf.slice(12)}` : '****************';
}
